import {
    type CalendarDate,
    getLocalTimeZone,
    getWeeksInMonth,
    today,
} from "@internationalized/date";
import type { DateValue } from "@react-aria/calendar";
import { useLocale } from "@react-aria/i18n";
import { useRouter } from "@tanstack/react-router";
import { Button } from "@workspace/web-ui/components/button";
import { Card } from "@workspace/web-ui/components/card";
import * as React from "react";
import { Calendar } from "~/features/appointments/components";
import { FormPanel } from "~/features/appointments/components/form-panel";
import { LeftPanel } from "~/features/appointments/components/left-panel";
import { RightPanel } from "~/features/appointments/components/right-panel";
import { AppointmentFormData } from "../schemas";
import { useAppointmentFormStore } from "../store/use-appointment-store";
import { useCreateAppointmentBookingMutation } from "../api/mutations";
import { useAuth } from "~/providers/auth.provider";
import { FileRouteTypes } from "~/routeTree.gen"

type AppointmentBookingProps = {
    selectedDate?: string;
    selectedSlot?: string;
    to: FileRouteTypes["to"];
    successUrl?: string;
    errorUrl?: string;
}

export function AppointmentBooking({ selectedDate, selectedSlot, to, successUrl, errorUrl }: AppointmentBookingProps) {
    const router = useRouter();
    const auth = useAuth();
    const { locale } = useLocale();

    const { step, setStep, setAppointment } = useAppointmentFormStore();
    const { mutate: createAppointmentBooking, isPending: isCreatingAppointmentBooking } = useCreateAppointmentBookingMutation();

    const [timeZone, setTimeZone] = React.useState("America/New_York");
    const [date, setDate] = React.useState(today(getLocalTimeZone()));
    const [focusedDate, setFocusedDate] = React.useState<CalendarDate | null>(date);

    React.useEffect(() => {
        if (!selectedDate) {
            const currentDate = date.toDate(timeZone);
            router.navigate({
                to,
                search: {
                    date: currentDate.toISOString().split("T")[0],
                    slot: selectedSlot
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    const handleSubmit = (data: AppointmentFormData) => {
        // Parse the slot time in the selected timezone
        const slotDate = new Date(selectedSlot!);
        const timeString = slotDate.toLocaleTimeString('en-US', {
            hour12: false,
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });

        createAppointmentBooking({
            title: "Routine Checkup",
            description: data.notes ?? undefined,
            location: "zoom",
            date: new Date(selectedDate!).toISOString().split('T')[0] || "",
            time: timeString,
            timezone: timeZone,
            name: data.name,
            email: data.email,
            phone: data.phone,
            success_url: successUrl,
            cancel_url: errorUrl,
            program: 'weight_loss'
        }, {
            onSuccess: (response) => {
                if (response.success) {
                    setStep(1);
                    setAppointment(response.data.appointment);
                    window.location.replace(response.data.payment_metadata.url);
                }
            }
        });
    };

    const weeksInMonth = getWeeksInMonth(focusedDate as DateValue, locale);

    const handleChangeDate = (date: DateValue) => {
        setDate(date as unknown as CalendarDate);
        router.navigate({ to, search: { date: date.toDate(timeZone).toISOString().split("T")[0], slot: selectedSlot } });
    };

    const handleChangeAvailableTime = (time: string) => {
        const timeValue = time.split(":").join(" ");

        const match = timeValue.match(/^(\d{1,2}) (\d{2})([ap]m)?$/i);
        if (!match) {
            console.error("Invalid time format");
            return null;
        }

        let hours = Number.parseInt(match[1]!);
        const minutes = Number.parseInt(match[2]!);
        const isPM = match[3] && match[3].toLowerCase() === "pm";

        if (isPM && (hours < 1 || hours > 12)) {
            console.error("Time out of range (1-12) in 12-hour format");
            return null;
        }

        if (isPM && hours !== 12) {
            hours += 12;
        } else if (!isPM && hours === 12) {
            hours = 0;
        }

        const currentDate = date.toDate(timeZone);
        currentDate.setHours(hours, minutes);

        router.navigate({ to, search: { date: selectedDate, slot: currentDate.toISOString() } });
    };

    const selectedTime = selectedSlot ? new Date(selectedSlot).toLocaleString(locale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
    }).toLowerCase().replace(' ', '') : undefined;

    const showForm = !!selectedDate && !!selectedSlot;

    return (
        <div className="flex justify-center items-center w-full sm:px-0 lg:px-14 mb-10 max-w-full">
            <Card className="w-full flex flex-col h-full max-w-full">
                <div className="flex items-center justify-center py-4">
                    <h3 className="text-xl font-medium text-center text-[#2D2F35]"> {step === 1 ? "Set time and date" : "Confirm details"}</h3>
                </div>
                {step === 1 && (
                    <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:h-[350px] border-t border-b border-t-[#DCDEE3] border-b-[#DCDEE3]">
                        <Calendar
                            minValue={today(getLocalTimeZone()) as unknown as DateValue}
                            defaultValue={today(getLocalTimeZone()) as unknown as DateValue}
                            value={date as unknown as DateValue}
                            onChange={handleChangeDate}
                            onFocusChange={(focused) => setFocusedDate(focused as unknown as CalendarDate)}
                        />
                        <RightPanel
                            weeksInMonth={weeksInMonth}
                            timeZone={timeZone}
                            setTimeZone={setTimeZone}
                            handleChangeAvailableTime={handleChangeAvailableTime}
                            selectedTime={selectedTime}
                            selectedDate={date.toDate(timeZone)}
                        />
                    </div>
                )}

                {step === 2 && (
                    <div className="w-full grid grid-cols-1 sm:grid-cols-2 border-t border-b border-t-[#DCDEE3] border-b-[#DCDEE3]">
                        <LeftPanel
                            showForm={showForm}
                            timeZone={timeZone}
                            slot={selectedSlot}
                        />
                        <FormPanel
                            onSubmit={handleSubmit}
                            defaultValues={{
                                name: `${auth?.user?.first_name} ${auth?.user?.last_name}`,
                                email: auth?.user?.email,
                                phone: auth?.user?.phone_number,
                                notes: "",
                            }}
                        />
                    </div>
                )}

                <div className="flex items-center justify-end gap-2 py-4 px-4 lg:px-6">
                    {step === 1 && (
                        <Button disabled={!showForm} onClick={() => setStep(2)} className="w-full sm:w-auto h-10 p-3 rounded-lg bg-[#4272DD]">Continue</Button>
                    )}
                    {step === 2 && (
                        <>
                            <Button
                                variant="ghost"
                                onClick={() => {
                                    setStep(1);
                                    router.navigate({ to });
                                }}
                                className="w-full sm:w-auto h-10 py-3 px-5 border rounded-lg border-[#DCDCDE]"
                            >
                                Back
                            </Button>
                            <Button
                                type="submit"
                                form="appointment-form"
                                disabled={isCreatingAppointmentBooking}
                                className="w-full sm:w-auto h-10 py-3 px-5 rounded-lg bg-[#4272DD]"
                            >
                                {isCreatingAppointmentBooking ? "Confirming..." : "Confirm"}
                            </Button>
                        </>
                    )}
                </div>
            </Card>
        </div>
    );
}
